// frontend/src/workshop/data/mock-journeys.ts

import type { CursorData } from "@/workshop/components/cursors/agent-cursor-overlay";
import type { FlowStep } from "@/workshop/components/shapes/workflow-frame/types";
import type { MemoryNodeDetail, RelatedNode } from "@/workshop/components/shapes/memory-frame/types";
import { GRAPH_NODE_COLORS } from "@/workshop/components/shapes/memory-frame/types";

// ─── Shared types ──────────────────────────────────────────────────────────────

export interface ToolCallData {
  id: string;
  name: string;
  status: "done" | "running" | "pending";
}

export interface AgentStreamData {
  id: string;
  agentName: string;
  agentColor: string;
  toolCalls: ToolCallData[];
  streamingText: string;
  isLive: boolean;
}

export interface WiringEvent {
  type: "ws" | "rest";
  name: string;
  description: string;
}

export interface CommentMessage {
  id: string;
  author: "user" | "agent";
  agentName?: string;
  text: string;
  timestamp: string;
}

export interface OAuthApp {
  id: string;
  name: string;
  icon: string;
  status: "connected" | "needs-auth";
}

export interface ScheduleRun {
  id: string;
  timestamp: string;
  status: "success" | "failed" | "running";
  duration: string;
}

export interface AgentTeamMember {
  id: string;
  name: string;
  letter: string;
  color: string;
  specialty: string;
  status: "active" | "idle";
}

export interface SessionData {
  id: string;
  title: string;
  date: string;
  agents: string[];
  frameCount: number;
}

// ─── Page 1: Canvas Work Session ──────────────────────────────────────────────

export const MOCK_CANVAS_SESSION = {
  cursors: [
    { id: "aria", name: "Aria", color: "#30D158", x: 310, y: 120, toolCall: "write_frame" },
    { id: "rex",  name: "Rex",  color: "#bf5af2", x: 560, y: 140, toolCall: "web_search" },
  ] satisfies CursorData[],

  agentStream: [
    {
      id: "aria",
      agentName: "Aria",
      agentColor: "#30D158",
      isLive: true,
      toolCalls: [
        { id: "t1", name: 'web_search("AI content trends 2025")', status: "done" },
        { id: "t2", name: 'read_frame("market-research")',        status: "done" },
        { id: "t3", name: 'write_frame("blog-outline")',          status: "running" },
      ],
      streamingText: "## Introduction\nThe future of AI-powered content marketing is",
    },
    {
      id: "rex",
      agentName: "Rex",
      agentColor: "#bf5af2",
      isLive: true,
      toolCalls: [
        { id: "t4", name: 'web_search("email templates SaaS 2025")', status: "running" },
      ],
      streamingText: "Analyzing 8 results…",
    },
  ] satisfies AgentStreamData[],

  wiring: [
    { type: "ws",   name: "agent.token",     description: "Each token streamed → append to frame content" },
    { type: "ws",   name: "agent.spawned",   description: "New agent created → add cursor, header chip, sidebar row" },
    { type: "ws",   name: "agent.tool_call", description: "Tool invoked → show running chip in execution panel" },
    { type: "ws",   name: "cursor.move",     description: "Agent cursor position update every 100 ms" },
    { type: "ws",   name: "frame.status",    description: "Frame status change → update frame border + sidebar icon" },
    { type: "rest", name: "POST /tasks",     description: "User submits task → create session, dispatch to orchestrator" },
  ] satisfies WiringEvent[],
};

// ─── Page 2: Controls & Trust ─────────────────────────────────────────────────

export const MOCK_CONTROLS_TRUST = {
  approval: {
    action: "Send email campaign",
    description: "Aria wants to send the drafted newsletter to 2,400 subscribers via Mailchimp. This action cannot be undone.",
    riskLevel: "high" as const,
  },

  commentThread: [
    { id: "c1", author: "user",  text: "Make the intro shorter — we need to hook them in 2 sentences max.", timestamp: "2 min ago" },
    { id: "c2", author: "agent", agentName: "Aria", text: "Got it — rewriting the intro now. I'll aim for a punchy opening under 40 words.", timestamp: "1 min ago" },
  ] satisfies CommentMessage[],

  wiring: [
    { type: "ws",   name: "workflow.paused",          description: "Workflow hit approval gate → surface ApprovalCard on canvas" },
    { type: "ws",   name: "approval.requested",       description: "Approval metadata → populate Approval right-panel tab" },
    { type: "ws",   name: "comment.added",             description: "New comment on a frame → show CommentBubble badge" },
    { type: "rest", name: "POST /approvals/:id/approve", description: "User approves → resume workflow execution" },
    { type: "rest", name: "POST /approvals/:id/reject",  description: "User rejects → halt workflow, notify agent" },
    { type: "rest", name: "POST /frames/:id/comments",   description: "User submits comment → save + dispatch to agent" },
  ] satisfies WiringEvent[],
};

// ─── Page 3: Extend & Automate ────────────────────────────────────────────────

export const MOCK_EXTEND_AUTOMATE = {
  oauthApps: [
    { id: "linkedin", name: "LinkedIn", icon: "Li", status: "needs-auth" },
    { id: "gmail",    name: "Gmail",    icon: "G",  status: "connected"  },
    { id: "notion",   name: "Notion",   icon: "N",  status: "connected"  },
    { id: "github",   name: "GitHub",   icon: "GH", status: "needs-auth" },
    { id: "slack",    name: "Slack",    icon: "S",  status: "connected"  },
  ] satisfies OAuthApp[],

  scheduleRuns: [
    { id: "r1", timestamp: "Mon Mar 10 · 9:00 am", status: "success", duration: "1m 23s" },
    { id: "r2", timestamp: "Mon Mar 03 · 9:00 am", status: "success", duration: "1m 41s" },
    { id: "r3", timestamp: "Mon Feb 24 · 9:01 am", status: "failed",  duration: "0m 12s" },
    { id: "r4", timestamp: "Mon Feb 17 · 9:00 am", status: "success", duration: "1m 18s" },
    { id: "r5", timestamp: "Mon Feb 10 · 9:00 am", status: "success", duration: "1m 55s" },
  ] satisfies ScheduleRun[],

  agentTeam: [
    { id: "aria", name: "Aria", letter: "A", color: "#30D158", specialty: "Writer · researcher",   status: "active" },
    { id: "rex",  name: "Rex",  letter: "R", color: "#bf5af2", specialty: "Coder · email builder", status: "idle"   },
    { id: "max",  name: "Max",  letter: "M", color: "#0a84ff", specialty: "Analyst · data",        status: "idle"   },
  ] satisfies AgentTeamMember[],

  wiring: [
    { type: "rest", name: "GET /oauth/providers",         description: "List available OAuth apps + connection status" },
    { type: "rest", name: "POST /oauth/connect",          description: "Initiate OAuth flow → returns redirect URL" },
    { type: "ws",   name: "oauth.connected",              description: "OAuth complete → unblock agent, resume task" },
    { type: "ws",   name: "agent.blocked",                description: "Agent needs tool → surface OAuthPromptCard" },
    { type: "rest", name: "POST /workflows/:id/schedule", description: "Set cron schedule on a workflow" },
    { type: "rest", name: "GET /workflows/:id/runs",      description: "Fetch run history for schedule panel" },
    { type: "rest", name: "GET /agents/catalog",          description: "Agent catalog for team builder" },
    { type: "rest", name: "POST /agents",                 description: "Add agent to user's team" },
  ] satisfies WiringEvent[],
};

// ─── Canvas session — frame data ──────────────────────────────────────────────

export interface SearchResultItem {
  title: string;
  snippet: string;
  url: string;
  image?: string;
  isTopPick?: boolean;
}

export const MOCK_SEARCH_RESULTS: SearchResultItem[] = [
  {
    title: "AI Content Marketing Trends 2025 — State of the Industry",
    snippet: "Brands using AI-assisted content production report a 3× increase in output velocity with only a 12% increase in cost, according to the 2025 Content Benchmark Report.",
    url: "https://contentmarketinginstitute.com/ai-trends-2025",
    isTopPick: true,
  },
  {
    title: "How to Build a Scalable AI Content Pipeline",
    snippet: "The key is a human-in-the-loop review layer: agents draft, humans approve, agents publish. This hybrid workflow reduces error rates by 67%.",
    url: "https://hubspot.com/blog/ai-content-pipeline",
  },
  {
    title: "Generative AI Tools Compared: GPT-4o vs Claude vs Gemini for Marketing",
    snippet: "For long-form blog content, Claude 3.5 Sonnet outperformed other models on brand tone consistency in blind evaluations run by 14 marketing agencies.",
    url: "https://marketingweek.com/ai-tools-comparison",
  },
];

export const MOCK_WORKFLOW_STEPS = [
  {
    id: "wf1",
    type: "trigger",
    title: "Weekly trigger",
    icon: "Zap",
    status: "completed",
    after: null,
    durationMs: 0,
  },
  {
    id: "wf2",
    type: "agent_task",
    title: "Research trends",
    icon: "Bot",
    status: "completed",
    after: "wf1",
    durationMs: 18400,
  },
  {
    id: "wf3",
    type: "agent_task",
    title: "Draft blog post",
    icon: "Bot",
    status: "running",
    after: "wf2",
    liveStatus: "Aria writing intro…",
  },
  {
    id: "wf4",
    type: "approval_gate",
    title: "Review draft",
    icon: "Shield",
    status: "pending",
    after: "wf3",
  },
  {
    id: "wf5",
    type: "tool_call",
    title: "Publish to CMS",
    icon: "Send",
    status: "pending",
    after: "wf4",
  },
] satisfies FlowStep[];

export interface ConversationChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
}

export const MOCK_CONVERSATION_MESSAGES: ConversationChatMessage[] = [
  {
    id: "cm1",
    role: "user",
    content: "Research AI content trends for 2025 and draft a blog post. @Rex can you handle the email campaign draft at the same time?",
  },
  {
    id: "cm2",
    role: "assistant",
    content: "On it — I'll run the web search first, then start the draft. Handing the email brief to Rex now.",
  },
  {
    id: "cm3",
    role: "user",
    content: "Great. Make sure the intro is punchy — under 60 words.",
  },
  {
    id: "cm4",
    role: "assistant",
    content: "Got it. Keeping intro tight. Rex is already pulling SaaS email templates — should have a draft for you in about 90 seconds.",
  },
];

// ─── Page 4: Memory & History ─────────────────────────────────────────────────

export const MOCK_MEMORY_HISTORY = {
  sessions: [
    { id: "s1", title: "Marketing Sprint",   date: "3 days ago",  agents: ["Aria", "Rex"], frameCount: 7  },
    { id: "s2", title: "Dev Work — Auth",    date: "1 week ago",  agents: ["Rex"],         frameCount: 3  },
    { id: "s3", title: "Q1 Research Digest", date: "2 weeks ago", agents: ["Aria", "Max"], frameCount: 11 },
    { id: "s4", title: "Product Roadmap",    date: "3 weeks ago", agents: ["Aria"],        frameCount: 5  },
  ] satisfies SessionData[],

  wiring: [
    { type: "rest", name: "GET /memory/nodes",         description: "Fetch knowledge graph nodes for memory panel" },
    { type: "rest", name: "GET /sessions",              description: "List past sessions for history panel" },
    { type: "rest", name: "POST /sessions/:id/resume",  description: "Restore session state to canvas" },
    { type: "ws",   name: "memory.recalled",            description: "Agent referenced memory → show MemoryBadge on canvas" },
    { type: "ws",   name: "session.restored",           description: "Session restore complete → canvas populates with frames" },
  ] satisfies WiringEvent[],
};

// ─── Memory node mock data ─────────────────────────────────────────────────────

export const MOCK_MEMORY_NODE: MemoryNodeDetail = {
  id: "node-ai-content-marketing",
  name: "AI Content Marketing",
  label: "AI Content Marketing",
  type: "concept",
  color: GRAPH_NODE_COLORS.concept,
  val: 3,
  description: "Strategies and tooling for using AI agents to automate and enhance content marketing workflows — research, drafting, personalisation, and distribution.",
  mention_count: 12,
  source_type: "auto_discovered",
  updated_at: "2026-03-08T14:00:00Z",
  metadata: {
    domain: "Marketing",
    confidence_score: 0.91,
  },
};

export const MOCK_RELATED_NODES: RelatedNode[] = [
  {
    id: "node-aria",
    name: "Aria",
    type: "person",
    color: GRAPH_NODE_COLORS.person,
    relationship: "used by",
    direction: "incoming",
    claimText: "Aria runs the content pipeline for this concept",
    confidence: 0.95,
    usageCount: 12,
    correctionCount: 0,
    lastVerified: "2026-03-08T12:00:00Z",
    sources: [
      {
        type: "conversation",
        confidence: 0.95,
        metadata: {
          llm_reasoning: "Aria was explicitly mentioned as running AI content workflows across 12 sessions",
          message_snippet: "Aria, draft a blog post on AI content trends",
          similar_mentions: 12,
        },
        timestamp: "2026-03-08T12:00:00Z",
      },
    ],
  },
  {
    id: "node-marketing-sprint",
    name: "Marketing Sprint",
    type: "project",
    color: GRAPH_NODE_COLORS.project,
    relationship: "part of",
    direction: "outgoing",
    confidence: 0.88,
    usageCount: 7,
    correctionCount: 1,
    lastVerified: "2026-03-06T09:30:00Z",
    sources: [
      {
        type: "document_extraction",
        confidence: 0.88,
        metadata: {
          llm_reasoning: "AI Content Marketing was the primary topic across 7 frames in the Marketing Sprint session",
          entity_extraction_confidence: 0.9,
        },
        timestamp: "2026-03-06T09:30:00Z",
      },
    ],
  },
  {
    id: "node-content-pipeline",
    name: "Content Pipeline",
    type: "process",
    color: GRAPH_NODE_COLORS.process,
    relationship: "enables",
    direction: "outgoing",
    confidence: 0.82,
    usageCount: 5,
    correctionCount: 0,
    lastVerified: "2026-03-07T16:00:00Z",
  },
];
